<template>
  <div class="adventure-details w-full px-16 md:px-0 md:max-w-580">
    <Breadcrumb v-if="breadcrumbLink" :header="breadcrumbHeader" :to="breadcrumbLink" />
    <div class="adventure-details-intro border-b-1 border-solid border-grey-divider-subdued pb-16">
      <img
        :src="adventureCategoryImageUrl + '?w=580'"
        class="w-screen object-cover -mx-16 my-24 h-144 md:w-full md:mx-0 md:h-208 md:rounded-xl"
        alt=""
      />
      <h1 class="text-h4 mb-4 break-words">{{ title }}</h1>
      <p class="text-body mb-4">Led by {{ sitterFirstName }} {{ sitterLastInitial }}.</p>
      <p class="text-body">
        <span>{{ city }}, {{ state }}</span>
        <span aria-hidden="true" class="mx-4">•</span>
        <span>Will travel {{ sitterTravelDistance }} {{ sitterTravelDistance == 1 ? 'mile' : 'miles' }}</span>
      </p>
    </div>

    <div class="adventure-details-rate border-b-1 border-solid border-grey-divider-subdued py-16">
      <div class="flex justify-between pb-8">
        <p aria-hidden="true" class="flex align-top">
          <span class="text-caption text-grey-text-subdued">$</span>
          <span class="text-h4">{{ sitterPayRateRange }}</span>
          <span class="text-grey-text-subdued">/hr</span>
        </p>
        <p class="sr-only">{{ srOnlyPayRate }}</p>
        <p class="text-grey-text-subdued">{{ durationLabel }}</p>
      </div>
      <p aria-hidden="true" class="text-body">+ ${{ computedSurcharge }}/child surcharge</p>
      <p class="sr-only">{{ srOnlySurchargeLabel }}</p>
    </div>

    <slot />

    <div class="adventure-details-body mt-24 mb-32 break-words">
      <h2 class="text-h4 mb-12">About this Adventure</h2>
      <ul class="mb-16">
        <li v-for="(listItem, index) in adventureDetailsList" :key="index" class="flex text-body items-center mb-8">
          <TickIcon color="#FF0064" aria-hidden="true" class="mr-8" />
          <span>{{ listItem }}</span>
        </li>
      </ul>
      <p class="mb-16 text-body">{{ description }}</p>
      <p class="mb-24 text-body">{{ sitterPassion }}</p>

      <h3 class="text-strong-body mb-4">What {{ sitterFirstName }} provides</h3>
      <ul class="list-disc pl-24 mb-24">
        <li v-for="(sitterItem, index) in adventureSitterSupplies" :key="index" class="mb-8 text-body">
          {{ sitterItem }}
        </li>
      </ul>

      <h3 class="text-strong-body mb-4">What parents provide:</h3>
      <ul class="list-disc pl-24 mb-24">
        <li v-for="(parentItem, index) in adventureParentSupplies" :key="index" class="mb-8 text-body">
          {{ parentItem }}
        </li>
      </ul>
    </div>

    <div class="border-b-1 border-solid border-grey-divider-subdued pb-32">
      <h2 class="text-h4 mb-12">Availability</h2>
      <AvailabilityTable class="w-full" :availability="availability" />
    </div>
  </div>
</template>

<script lang="ts">
// components
import Breadcrumb from '@/components/breadcrumb/Breadcrumb.vue';
import TickIcon from '@/graphics/icons/TickIcon.vue';
import AvailabilityTable from '../forms/availability/AvailabilityTable.vue';

export default {
  name: 'Details',
  components: { Breadcrumb, TickIcon, AvailabilityTable },
  props: {
    breadcrumbHeader: { type: String, default: '' },
    breadcrumbLink: { type: String, default: '' },
    adventureCategoryImageUrl: {
      required: true,
      type: String,
    },
    title: {
      required: true,
      type: String,
    },
    sitterFirstName: {
      required: true,
      type: String,
    },
    sitterLastInitial: {
      required: true,
      type: String,
    },
    city: {
      required: true,
      type: String,
    },
    state: {
      required: true,
      type: String,
    },
    sitterTravelDistance: {
      required: true,
      type: Number,
    },
    minPayRate: {
      required: true,
      type: Number,
    },
    maxPayRate: {
      required: true,
      type: Number,
    },
    duration: {
      required: true,
      type: Number,
    },
    surcharge: {
      required: true,
      type: Number,
    },
    ageRangeText: {
      required: true,
      type: String,
    },
    adventureCategoryName: {
      required: true,
      type: String,
    },
    description: {
      required: true,
      type: String,
    },
    sitterPassion: {
      required: true,
      type: String,
    },
    adventureSitterSupplies: {
      required: true,
      type: Array,
    },
    adventureParentSupplies: {
      required: true,
      type: Array,
    },
    sitterAvailability: {
      required: true,
      type: Object,
    },
  },
  computed: {
    sitterPayRateRange(): string {
      return `${Math.floor(this.minPayRate / 100)}–${Math.floor(this.maxPayRate / 100)}`;
    },
    srOnlyPayRate(): string {
      return `${this.sitterPayRateRange} dollars per hour`;
    },
    durationLabel(): string {
      return `${Math.floor(this.duration / 60)} hour minimum`;
    },
    computedSurcharge(): number {
      return Math.floor(this.surcharge / 100);
    },
    srOnlySurchargeLabel(): string {
      return `Plus $${this.computedSurcharge} per child surcharge`;
    },
    ageRangeLabel(): string {
      return `For ages ${this.ageRangeText}`;
    },
    adventureDetailsList(): Array<any> {
      return [`${this.adventureCategoryName}`, `${this.ageRangeLabel}`, `${this.durationLabel}`];
    },
    availability(): Object {
      return this.sitterAvailability || {};
    },
  },
};
</script>
